// Чистая логика карточки токена роли: подписи, цвета и набор действий
// определяются статусом и режимом выпуска строки.

import type { RepoHostTokenSummary, TokenStatus } from "./tokensApi";

// Живые строки роли: ими Baton пользуется (максимум одна на роль).
export const LIVE_TOKEN_STATUSES: ReadonlySet<TokenStatus> = new Set([
  "active",
  "expiring",
]);

// Статусы, которыми управляет DELETE роли.
export const MANAGEABLE_TOKEN_STATUSES: ReadonlySet<TokenStatus> = new Set([
  "active",
  "expiring",
  "parked",
]);

export const TOKEN_STATUS_COLOR: Readonly<Record<TokenStatus, string>> = {
  active: "green",
  expiring: "yellow",
  parked: "orange",
  superseded: "blue",
  expired: "red",
  revoked: "gray",
  relinquished: "red",
};

export const TOKEN_STATUS_LABEL_KEY: Readonly<Record<TokenStatus, string>> = {
  active: "connections.tokens.statusActive",
  expiring: "connections.tokens.statusExpiring",
  parked: "connections.tokens.statusParked",
  superseded: "connections.tokens.statusSuperseded",
  expired: "connections.tokens.statusExpired",
  revoked: "connections.tokens.statusRevoked",
  relinquished: "connections.tokens.statusRelinquished",
};

export const TOKEN_STATUS_HINT_KEY: Readonly<
  Partial<Record<TokenStatus, string>>
> = {
  superseded: "connections.tokens.statusHint.superseded",
  parked: "connections.tokens.statusHint.parked",
};

export interface TokenCardActions {
  readonly probe: boolean;
  readonly guide: boolean;
  readonly replace: boolean;
  readonly rotate: boolean;
  readonly rotationInterval: boolean;
  readonly remove: boolean;
}

export interface KeyedToken {
  readonly key: string;
  readonly token: RepoHostTokenSummary;
}

export function tokenCardActions(
  token: RepoHostTokenSummary,
  deletable: boolean,
): TokenCardActions {
  const live = LIVE_TOKEN_STATUSES.has(token.status);
  const batonIssued = token.issuance_mode === "baton_issued";
  return {
    probe: live,
    guide: live,
    replace: live && !batonIssued,
    rotate: live && batonIssued,
    rotationInterval: live && batonIssued,
    remove: deletable && MANAGEABLE_TOKEN_STATUSES.has(token.status),
  };
}

// Зеркало `TokenManagementRepository.lock_role_credential`: цель DELETE роли —
// последняя строка роли среди управляемых статусов. Опора на порядок
// `GET /tokens` (`role_name, id`; id — UUIDv7, порядок = порядок создания).
export function deleteTargets(
  tokens: readonly RepoHostTokenSummary[],
): ReadonlySet<RepoHostTokenSummary> {
  const lastByRole = new Map<string, RepoHostTokenSummary>();
  for (const token of tokens) {
    if (
      token.role_name !== null &&
      MANAGEABLE_TOKEN_STATUSES.has(token.status)
    ) {
      lastByRole.set(token.role_name, token);
    }
  }
  return new Set(lastByRole.values());
}

export function rolesWithLiveToken(
  tokens: readonly RepoHostTokenSummary[],
): ReadonlySet<string> {
  const roles = new Set<string>();
  for (const token of tokens) {
    if (token.role_name !== null && LIVE_TOKEN_STATUSES.has(token.status)) {
      roles.add(token.role_name);
    }
  }
  return roles;
}

export function keyedTokens(
  tokens: readonly RepoHostTokenSummary[],
): readonly KeyedToken[] {
  const seen = new Map<string, number>();
  return tokens.map((token) => {
    const group = token.role_name ?? token.host_username;
    const index = seen.get(group) ?? 0;
    seen.set(group, index + 1);
    return { key: `${group}:${index}`, token };
  });
}
